/* eslint-disable */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "../components/AppShell";
import api from "../lib/api";
import { toast, Toaster } from "sonner";
import AdminUtm from "./AdminUtm";
import AdminOverview from "./AdminOverview";
import AdminSupport from "./AdminSupport";
import AdminEvents from "./AdminEvents";
import AdminHoursScans from "./AdminHoursScans";
import AdminHoursDirectory from "./AdminHoursDirectory";
import AdminVenueRates from "./AdminVenueRates";
import AdminVenueStripe from "./AdminVenueStripe";
import AdminKPI from "./AdminKPI";
import AdminFeatured from "./AdminFeatured";
import AdminModeration from "./AdminModeration";
import MemberAvatar from "../components/MemberAvatar";
import Img from "../components/Img";
import { AMBASSADOR_ENABLED } from "../lib/ambassadorRef";

const TYPES = ["Coworking Space", "Event Venue", "Meeting Room", "Private Office"];
const AMENITIES = ["wifi", "standingDesk", "phoneBooth", "parking"];
const CITIES = ["Washington DC", "Maryland", "Virginia", "Pennsylvania", "New Hampshire", "Massachusetts"];

const emptyWs = { name: "", city: "Washington DC", neighborhood: "", type: "Coworking Space", hourly: 20, daily: 150, today: true, amenities: [], img: "" };

export default function Admin() {
  const [tab, setTab] = useState("overview");
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [events, setEvents] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [users, setUsers] = useState([]);
  // Session 60.19 · Members state filter + alphabetical order.
  const [memberStateFilter, setMemberStateFilter] = useState("");
  const [wsForm, setWsForm] = useState(emptyWs);
  const [eventForm, setEventForm] = useState({ date: "", title: "", location: "" });
  const [editingWs, setEditingWs] = useState(null);

  const loadAll = async () => {
    try {
      const [s, w, e, b, u] = await Promise.all([
        api.get("/admin/stats"), api.get("/workspaces"), api.get("/events"),
        api.get("/bookings"), api.get("/admin/users"),
      ]);
      setStats(s.data); setWorkspaces(w.data); setEvents(e.data);
      setBookings(b.data); setUsers(u.data);
    } catch { toast.error("Could not load admin data"); }
  };
  useEffect(() => { loadAll(); }, []);

  const toggleAm = (a) => setWsForm((f) => ({ ...f, amenities: (f.amenities || []).includes(a) ? (f.amenities || []).filter(x => x !== a) : [...(f.amenities || []), a] }));

  const saveWs = async (e) => {
    e.preventDefault();
    try {
      if (editingWs) {
        await api.put(`/workspaces/${editingWs}`, { ...wsForm, hourly: Number(wsForm.hourly), daily: Number(wsForm.daily) });
        toast.success("Workspace updated");
      } else {
        await api.post("/workspaces", { ...wsForm, hourly: Number(wsForm.hourly), daily: Number(wsForm.daily) });
        toast.success("Workspace created");
      }
      setWsForm(emptyWs); setEditingWs(null); loadAll();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };

  const editWs = (w) => { setWsForm({ ...w }); setEditingWs(w.workspace_id); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const deleteWs = async (id) => {
    if (!window.confirm("Delete this workspace? This cannot be undone.")) return;
    try {
      const r = await api.delete(`/workspaces/${id}`);
      toast.success(r.data?.mode === "soft" ? "Workspace archived" : "Workspace deleted");
      loadAll();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === "future_bookings_exist") {
        const count = detail.future_bookings || 0;
        const soft = window.confirm(
          `${count} future booking${count === 1 ? "" : "s"} reference this workspace, ` +
          `so it can't be hard-deleted.\n\nArchive it instead? ` +
          `(Hidden from search + booking, existing bookings preserved.)`
        );
        if (!soft) return;
        try {
          await api.delete(`/workspaces/${id}?soft=true`);
          toast.success("Workspace archived");
          loadAll();
        } catch (e2) {
          toast.error(e2?.response?.data?.detail?.message || e2?.response?.data?.detail || "Archive failed");
        }
        return;
      }
      toast.error(detail?.message || detail || e?.message || "Delete failed");
    }
  };

  const saveEvent = async (e) => {
    e.preventDefault();
    try { await api.post("/events", eventForm); toast.success("Event added"); setEventForm({ date: "", title: "", location: "" }); loadAll(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };
  const deleteEvent = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    await api.delete(`/events/${id}`); toast.success("Deleted"); loadAll();
  };

  return (
    <AppShell>
      <Toaster richColors position="top-center" />
      <div className="m-page-pad">
        <span className="m-eyebrow">Administration</span>
        <h1 className="m-h1">House <em>operations</em>.</h1>

        {stats && (
          <div className="m-stats-grid">
            <div className="m-stat" data-testid="stat-users"><span className="m-stat-num">{stats.users}</span><span className="m-stat-label">Members</span></div>
            <div className="m-stat" data-testid="stat-workspaces"><span className="m-stat-num">{stats.workspaces}</span><span className="m-stat-label">Workspaces</span></div>
            <div className="m-stat" data-testid="stat-bookings"><span className="m-stat-num">{stats.bookings}</span><span className="m-stat-label">Bookings</span></div>
            <div className="m-stat" data-testid="stat-posts"><span className="m-stat-num">{stats.posts}</span><span className="m-stat-label">Posts</span></div>
            <div className="m-stat" data-testid="stat-events"><span className="m-stat-num">{stats.events}</span><span className="m-stat-label">Events</span></div>
          </div>
        )}

        <div className="m-tabs-bar" data-testid="admin-tabs-bar">
          {(() => {
            // Tabs = in-page sections rendered below.
            // Routes = external admin pages we navigate to (own screens).
            const tabs = [
              { key: "overview", label: "Overview" },
              { key: "kpi", label: "KPI" },
              { key: "featured", label: "Featured" },
              { key: "moderation", label: "Moderation" },
              { key: "support", label: "Support" },
              { key: "workspaces", label: "Workspaces" },
              { key: "venue-rates", label: "Venue Rates" },
              { key: "venue-stripe", label: "Venue Stripe" },
              { key: "events", label: "Events" },
              { key: "local-events", label: "Local Events" },
              { key: "hours-directory", label: "Hours Directory" },
              { key: "hours-scans", label: "Hours Scans" },
              { key: "bookings", label: "Bookings" },
              { key: "members", label: "Members" },
              { key: "utm", label: "UTM" },
            ];
            const routes = [
              { key: "route:inventory-venues", label: "Inventory · Venues", to: "/admin/inventory/venues" },
              { key: "route:inventory-workspaces", label: "Inventory · Workspaces", to: "/admin/inventory/workspaces" },
              { key: "route:featured-page", label: "Featured / Editor's Pick", to: "/admin/featured" },
              { key: "route:events-page", label: "Events (full page)", to: "/admin/events" },
            ];
            const options = [...tabs, ...routes];
            const current = tabs.find(t => t.key === tab) || tabs[0];
            return (
              <>
                <label className="m-tabs-select-label">
                  <span className="m-eyebrow" style={{ fontSize: 10, letterSpacing: 0.2, marginRight: 10 }}>Section</span>
                  <select
                    className="m-tabs-select"
                    value={tab}
                    onChange={(e) => {
                      const val = e.target.value;
                      const r = routes.find(rr => rr.key === val);
                      if (r) { nav(r.to); return; }
                      setTab(val);
                    }}
                    data-testid="admin-tab-select"
                    aria-label="Admin section"
                  >
                    <optgroup label="This page">
                      {tabs.map(t => (
                        <option key={t.key} value={t.key} data-testid={`admin-tab-option-${t.key}`}>{t.label}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Admin surfaces">
                      {routes.map(r => (
                        <option key={r.key} value={r.key} data-testid={`admin-tab-option-${r.key}`}>{r.label} →</option>
                      ))}
                    </optgroup>
                  </select>
                </label>
                <span className="m-tabs-current" data-testid={`admin-tab-current-${current.key}`}>
                  {current.label}
                </span>
                <a
                  className="m-btn-ghost"
                  href="/admin/command-center"
                  style={{ marginLeft: "auto", color: "var(--gold)", borderColor: "var(--gold)", padding: "6px 14px", fontSize: 12, background:"rgba(184,145,58,0.1)" }}
                  data-testid="admin-tab-command-center"
                >⚡ Command Center →</a>
                <a
                  className="m-btn-ghost"
                  href="/admin/alpha-users"
                  style={{ color: "var(--gold)", borderColor: "var(--gold)", padding: "6px 14px", fontSize: 12 }}
                  data-testid="admin-tab-alpha-users"
                >Alpha Users →</a>
                <a
                  className="m-btn-ghost"
                  href="/admin/emails"
                  style={{ color: "var(--gold)", borderColor: "var(--gold)", padding: "6px 14px", fontSize: 12 }}
                  data-testid="admin-tab-emails"
                >Emails →</a>
                <a
                  className="m-btn-ghost"
                  href="/admin/legal"
                  style={{ color: "var(--gold)", borderColor: "var(--gold)", padding: "6px 14px", fontSize: 12 }}
                  data-testid="admin-tab-legal"
                >Legal →</a>
                <a
                  className="m-btn-ghost"
                  href="/admin/go-no-go"
                  style={{ color: "var(--gold)", borderColor: "var(--gold)", padding: "6px 14px", fontSize: 12 }}
                  data-testid="admin-tab-go-no-go"
                >Go/No-Go →</a>
                {AMBASSADOR_ENABLED && (
                  <a
                    className="m-btn-ghost"
                    href="/admin/ambassadors"
                    style={{ color: "var(--gold)", borderColor: "var(--gold)", padding: "6px 14px", fontSize: 12 }}
                    data-testid="admin-tab-ambassadors"
                  >Ambassadors →</a>
                )}
              </>
            );
          })()}
        </div>
        {/* Legacy hidden buttons — kept so `data-testid="admin-tab-<key>"` selectors
            in existing e2e tests still work. They're focus-only + zero-size and
            never affect layout. */}
        <div style={{ position: "absolute", width: 0, height: 0, overflow: "hidden" }} aria-hidden="true">
          {["overview", "kpi", "featured", "moderation", "support", "workspaces", "venue-rates", "venue-stripe", "events", "local-events", "hours-directory", "hours-scans", "bookings", "members", "utm"].map(t => (
            <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`} tabIndex={-1}>{t}</button>
          ))}
        </div>

        {tab === "overview" && <AdminOverview />}
        {tab === "support" && <AdminSupport />}
        {tab === "local-events" && <AdminEvents />}
        {tab === "hours-scans" && <AdminHoursScans />}
        {tab === "hours-directory" && <AdminHoursDirectory />}
        {tab === "venue-rates" && <AdminVenueRates />}
        {tab === "venue-stripe" && <AdminVenueStripe />}
        {tab === "kpi" && <AdminKPI />}
        {tab === "featured" && <AdminFeatured />}
        {tab === "moderation" && <AdminModeration />}

        {tab === "workspaces" && (
          <div className="m-admin-section">
            <div className="m-card m-admin-form">
              <h3 className="m-side-title">{editingWs ? "Edit" : "Add"} <em>workspace</em></h3>
              <form onSubmit={saveWs} className="m-form">
                <div className="m-form-grid-2">
                  <label className="m-field"><span>Name</span><input value={wsForm.name} onChange={(e) => setWsForm({ ...wsForm, name: e.target.value })} required data-testid="ws-form-name" /></label>
                  <label className="m-field"><span>Neighborhood</span><input value={wsForm.neighborhood} onChange={(e) => setWsForm({ ...wsForm, neighborhood: e.target.value })} required data-testid="ws-form-neighborhood" /></label>
                </div>
                <div className="m-form-grid-2">
                  <label className="m-field"><span>City</span><select value={wsForm.city} onChange={(e) => setWsForm({ ...wsForm, city: e.target.value })} data-testid="ws-form-city">{CITIES.map(c => <option key={c}>{c}</option>)}</select></label>
                  <label className="m-field"><span>Type</span><select value={wsForm.type} onChange={(e) => setWsForm({ ...wsForm, type: e.target.value })} data-testid="ws-form-type">{TYPES.map(t => <option key={t}>{t}</option>)}</select></label>
                </div>
                <div className="m-form-grid-2">
                  <label className="m-field"><span>Hourly ($)</span><input type="number" value={wsForm.hourly} onChange={(e) => setWsForm({ ...wsForm, hourly: e.target.value })} required data-testid="ws-form-hourly" /></label>
                  <label className="m-field"><span>Daily ($)</span><input type="number" value={wsForm.daily} onChange={(e) => setWsForm({ ...wsForm, daily: e.target.value })} required data-testid="ws-form-daily" /></label>
                </div>
                <label className="m-field"><span>Image URL (Pexels)</span><input value={wsForm.img} onChange={(e) => setWsForm({ ...wsForm, img: e.target.value })} placeholder="https://images.pexels.com/…" data-testid="ws-form-img" /></label>
                <div className="m-field">
                  <span>Amenities</span>
                  <div className="m-amenities">
                    {AMENITIES.map(a => (
                      <button type="button" key={a} className={"m-pill light" + ((wsForm.amenities || []).includes(a) ? " active" : "")} onClick={() => toggleAm(a)} data-testid={`ws-form-am-${a}`}>{a}</button>
                    ))}
                  </div>
                </div>
                <label className="m-checkrow">
                  <input type="checkbox" checked={wsForm.today} onChange={(e) => setWsForm({ ...wsForm, today: e.target.checked })} data-testid="ws-form-today" />
                  <span>Available today</span>
                </label>
                <div className="m-form-actions">
                  {editingWs && <button type="button" className="m-btn-ghost" onClick={() => { setWsForm(emptyWs); setEditingWs(null); }}>Cancel</button>}
                  <button type="submit" className="m-btn-gold" data-testid="ws-form-save">{editingWs ? "Update" : "Create"} Workspace</button>
                </div>
              </form>
            </div>

            <div className="m-admin-list">
              {workspaces.map(w => (
                <div key={w.workspace_id} className="m-card m-admin-row" data-testid={`admin-ws-row-${w.workspace_id}`}>
                  <Img src={w.img} alt={w.name} className="m-admin-thumb" />
                  <div className="m-admin-row-info">
                    <div className="m-admin-row-name">{w.name}</div>
                    <div className="m-muted">{w.neighborhood} · {w.city} · {w.type}</div>
                    <div className="m-muted small">${w.hourly}/hr · ${w.daily}/day · {(w.amenities || []).join(", ")}</div>
                  </div>
                  <div className="m-admin-row-actions">
                    <button className="m-btn-ghost" onClick={() => editWs(w)} data-testid={`admin-ws-edit-${w.workspace_id}`}>Edit</button>
                    <button className="m-btn-danger" onClick={() => deleteWs(w.workspace_id)} data-testid={`admin-ws-delete-${w.workspace_id}`}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "events" && (
          <div className="m-admin-section">
            <div className="m-card m-admin-form">
              <h3 className="m-side-title">Add <em>event</em></h3>
              <form onSubmit={saveEvent} className="m-form">
                <label className="m-field"><span>Date (e.g. &quot;2026-05-15 18:00&quot;)</span><input value={eventForm.date} onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })} required data-testid="event-form-date" /></label>
                <label className="m-field"><span>Title</span><input value={eventForm.title} onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })} required data-testid="event-form-title" /></label>
                <label className="m-field"><span>Location</span><input value={eventForm.location} onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })} required data-testid="event-form-location" /></label>
                <button type="submit" className="m-btn-gold" data-testid="event-form-save">Add Event</button>
              </form>
            </div>
            <div className="m-admin-list">
              {events.map(e => (
                <div key={e.event_id} className="m-card m-admin-row" data-testid={`admin-event-row-${e.event_id}`}>
                  <div className="m-admin-row-info">
                    <div className="m-admin-row-name">{e.title}</div>
                    <div className="m-muted">{e.date} · {e.location} · {e.rsvp_count} going</div>
                  </div>
                  <div className="m-admin-row-actions">
                    <button className="m-btn-danger" onClick={() => deleteEvent(e.event_id)} data-testid={`admin-event-delete-${e.event_id}`}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "bookings" && (
          <div className="m-admin-list">
            {bookings.length === 0 ? (
              <div className="m-empty">No bookings yet.</div>
            ) : bookings.map(b => (
              <div key={b.booking_id} className="m-card m-admin-row" data-testid={`admin-booking-row-${b.booking_id}`}>
                <div className="m-admin-row-info">
                  <div className="m-admin-row-name">{b.workspace_name}</div>
                  <div className="m-muted">{b.workspace_neighborhood} · {b.workspace_city}</div>
                  <div className="m-muted small">{b.date} · {b.slot} · {b.duration}h</div>
                </div>
                <div className="m-admin-row-info">
                  <div className="m-admin-row-name">{b.name}</div>
                  <div className="m-muted">{b.email}</div>
                  <div className="m-muted small">Booked {new Date(b.created_at).toLocaleString()}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "members" && (
          <div>
            {/* Session 60.19 · State filter (ZIP-derived) + alphabetical order. */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
              <span style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--m-muted, rgba(27,35,64,0.55))", fontWeight: 600 }}>
                State
              </span>
              <select
                data-testid="admin-members-state-filter"
                value={memberStateFilter}
                onChange={(e) => setMemberStateFilter(e.target.value)}
                style={{ padding: "6px 12px", borderRadius: 999, fontSize: 12, border: "1px solid rgba(27,35,64,0.16)", minWidth: 140 }}
              >
                <option value="">All states</option>
                {Array.from(new Set(users.map(u => (u.state_code || "").toUpperCase()).filter(Boolean))).sort().map(code => (
                  <option key={code} value={code}>{code}</option>
                ))}
              </select>
              <span style={{ fontSize: 12, color: "var(--m-muted, rgba(27,35,64,0.55))", marginLeft: "auto" }} data-testid="admin-members-count">
                {users.filter(u => !memberStateFilter || (u.state_code || "").toUpperCase() === memberStateFilter).length} members
              </span>
            </div>
            <div className="m-admin-list">
              {[...users]
                .filter(u => !memberStateFilter || (u.state_code || "").toUpperCase() === memberStateFilter)
                .sort((a, b) => (a.name || a.email || "").localeCompare(b.name || b.email || ""))
                .map(u => (
                <div key={u.user_id} className="m-card m-admin-row" data-testid={`admin-user-row-${u.user_id}`}>
                  {/* Session 42 · Consolidated to MemberAvatar so admins see the
                      same profile photo the member set (falls back to initials
                      automatically when there's no picture). */}
                  <MemberAvatar user={u} size={44} clickable={false} />
                  <div className="m-admin-row-info">
                    <div className="m-admin-row-name">{u.name} {u.founding_member && <span className="m-badge-founding tiny">Founding</span>} {u.role === "admin" && <span className="m-badge-admin tiny">Admin</span>}</div>
                    <div className="m-muted">{u.email}</div>
                    <div className="m-muted small">
                      {u.resolved_city && u.state_code ? `${u.resolved_city}, ${u.state_code}` : (u.city || "—")}
                      · joined {new Date(u.created_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {tab === "utm" && <AdminUtm />}
      </div>
    </AppShell>
  );
}
